/**
 * VRG-549 — what each Studio control actually edits.
 *
 * The complaint this answers, verbatim: *"I have no idea what's affected on the app based
 * on what I change in the studio. I can see a change in the app, but I have to click
 * through every page to know how the page is changed."* That is a BLAST-RADIUS question,
 * and it has to be answerable **before** the operator changes anything — which is why the
 * fact table lives here as data rather than being discoverable by clicking around.
 *
 * ## What lives here, and what deliberately does not
 *
 * | | where | why |
 * |---|---|---|
 * | The DEFINITION of a control ("what is Primary color?") | `docs/glossary.json` | §44 — one definition, four consumers. Never retyped into a form. |
 * | The SURFACE LIST (which screens, which email parts) | here | A structured fact table, not a definition. It drives a panel, a count and a set of selectors; it is not prose anyone reads elsewhere. |
 * | The COUNT of what is highlighted right now | measured in the preview | See `apps/app/.../tokenHighlight.ts`. A number asserted here would rot. |
 *
 * ## The coverage limit is the point, not a footnote
 *
 * The certificate preview renders FOUR of the app's screens (`APP_FLOW_PAGES`); the app
 * registers 30 routes. So a highlight can only ever show part of a token's reach, and for
 * `primary_color` — which is read in ~100 places on every screen — it shows a small part
 * while *feeling* complete. That is the failure mode this file is designed against:
 * `offPreviewSurfaces` exists so the panel can name what it is NOT showing, and
 * `PREVIEWABLE_FLOW_COUNT` / `APP_SCREEN_COUNT` exist so it can state the ratio plainly.
 * A tool whose whole job is telling the truth about what is connected must not overstate
 * its own coverage.
 *
 * Sourced from the VRG-549 token survey (`.vergence/work/tasks/`), re-verified against
 * the code after `4638eb743` — which made `secondary_color`, the `@repo/ui` `Button` and
 * the `Switch` genuinely follow the tenant, so several of the survey's "this is dead"
 * findings are no longer true and the copy here describes the fixed behaviour.
 */

/** The seven theme tokens Studio exposes. Mirrors `StudioTokenName` in `apps/app`. */
export type StudioTokenName =
  | 'primary_color'
  | 'secondary_color'
  | 'text_color'
  | 'bg_color'
  | 'title_font'
  | 'font'
  | 'border_radius'

/** `?page=` tokens the certificate preview can render — the `value`s in `APP_FLOW_PAGES`. */
export type PreviewFlow =
  | 'certificates'
  | 'certificate'
  | 'claim-ownership'
  | 'purchase-product'

/**
 * Screens registered in the customer app, less the Studio preview harness itself.
 *
 * Derivation, so this can be re-checked rather than trusted: `apps/app/src/App.tsx`
 * references 30 distinct `ROUTES.*` values; one of them is `PREVIEW`, which is the
 * harness this pane loads and not a screen any customer reaches. Measured 2026-09-03.
 */
export const APP_SCREEN_COUNT = 29

/** How many of those the certificate preview can show. Keep in step with `APP_FLOW_PAGES`. */
export const PREVIEWABLE_FLOW_COUNT = 4

export interface TokenImpact {
  /** Glossary entry the control's description is read from. Never retype it (§44). */
  glossaryId: string
  /**
   * True when the token genuinely reaches every screen.
   *
   * A flag rather than a 29-item list, because enumerating every screen for four of the
   * seven tokens would bury the three where the list is the useful part. The panel says
   * "every screen" and then names a few the preview cannot reach.
   */
  appWide: boolean
  /** Previewable flows this token visibly changes. */
  previewableFlows: PreviewFlow[]
  /**
   * Screens it reaches that this preview CANNOT render, in operator words.
   *
   * For an `appWide` token these are examples, not an enumeration — the panel labels them
   * as such. For a scoped token they are the complete off-preview set.
   */
  offPreviewSurfaces: string[]
  /** What it reaches in a transactional email, in operator words. */
  emailReach: string
  /**
   * How the email behaviour DIFFERS from the app, when it does.
   *
   * Stated separately from `emailReach` because these are the four things the email
   * preview cannot show even in principle — a preview can render the light appearance
   * faithfully and still not tell you that dark mode discards the colour it just drew.
   */
  emailCaveat?: string
  /**
   * Selectors for the email preview highlight, resolved inside the rendered email's own
   * document.
   *
   * These are the `.v-*` classes `@repo/emails` `base-email.tsx` already emits for its
   * dark-mode block — an attribution map that happened to exist. Nothing was added to the
   * email HTML for this feature, which matters: annotation markup would otherwise ship in
   * every message a brand's customers receive.
   */
  emailParts: { label: string; selector: string }[]
}

export const TOKEN_IMPACT: Record<StudioTokenName, TokenImpact> = {
  primary_color: {
    glossaryId: 'studio-primary-color',
    appWide: true,
    previewableFlows: ['certificates', 'certificate', 'claim-ownership', 'purchase-product'],
    offPreviewSurfaces: [
      'Sign in',
      'Profile',
      'Notifications',
      'Activity',
      'Share a certificate',
      'Scan history',
      'Help',
      'My code',
    ],
    emailReach: 'Button fill only.',
    emailCaveat:
      'Email headings follow your text colour instead, the opposite of the app. In dark mode none of your colours are applied.',
    emailParts: [{ label: 'Buttons', selector: '.v-btn' }],
  },

  secondary_color: {
    glossaryId: 'studio-border-color',
    appWide: false,
    previewableFlows: ['certificate', 'certificates'],
    offPreviewSurfaces: ['Share a certificate', 'Scan history', 'Profile'],
    emailReach:
      'The rule under the header, the rule above the footer, section dividers, and the border around a verification code.',
    emailCaveat:
      'Borders carry further in email than they do in the app. In dark mode your border colour is ignored.',
    emailParts: [
      { label: 'Header rule, footer rule and dividers', selector: '.v-border' },
    ],
  },

  text_color: {
    glossaryId: 'studio-text-color',
    appWide: true,
    previewableFlows: ['certificates', 'certificate', 'claim-ownership', 'purchase-product'],
    offPreviewSurfaces: [
      'Sign in',
      'Profile',
      'Notifications',
      'Activity',
      'Share a certificate',
      'Help',
    ],
    emailReach: 'Body copy and headings.',
    emailCaveat:
      'In email this colours headings as well as body text. In the app headings follow your primary colour instead. Muted sub-labels stay a fixed grey. In dark mode your text colour is ignored.',
    emailParts: [{ label: 'Body text and headings', selector: '.v-text' }],
  },

  bg_color: {
    glossaryId: 'studio-background-color',
    appWide: false,
    previewableFlows: ['certificates', 'certificate', 'purchase-product'],
    offPreviewSurfaces: [
      'Share a certificate',
      'Scan history (and whether the scan map draws light or dark)',
    ],
    emailReach: 'The wall around the message.',
    emailCaveat:
      'It colours the area around the message, never the message card itself. The card stays white, so a dark background reads as a dark wall around a white card. In dark mode your background colour is ignored.',
    emailParts: [{ label: 'The wall around the message', selector: '.v-bg' }],
  },

  title_font: {
    glossaryId: 'studio-title-font',
    appWide: true,
    previewableFlows: ['certificates', 'certificate', 'claim-ownership', 'purchase-product'],
    offPreviewSurfaces: ['Sign in', 'Profile', 'Notifications', 'Help'],
    emailReach: 'The headline only. Everything else is the body font.',
    emailCaveat:
      'Fonts still apply in dark mode, along with corner radius and your logo.',
    emailParts: [
      // `EmailHeading` is the only element that writes `font-family` inline other than the
      // body wrapper, so this isolates it without needing a class the email does not have.
      { label: 'The headline', selector: '[style*="font-family"]:not(.v-bg)' },
    ],
  },

  font: {
    glossaryId: 'studio-body-font',
    appWide: true,
    previewableFlows: ['certificates', 'certificate', 'claim-ownership', 'purchase-product'],
    offPreviewSurfaces: ['Sign in', 'Profile', 'Notifications', 'Help', 'Scan history'],
    emailReach: 'The whole message, loaded as a web font.',
    emailCaveat:
      'The setting that carries most reliably into an inbox. Unlike your colours, it still applies in dark mode.',
    emailParts: [
      { label: 'Every line of text', selector: '.v-text, .v-muted, .v-btn' },
    ],
  },

  border_radius: {
    glossaryId: 'studio-border-radius',
    appWide: true,
    previewableFlows: ['certificates', 'certificate', 'claim-ownership', 'purchase-product'],
    offPreviewSurfaces: ['Sign in', 'Profile', 'Notifications', 'Share a certificate'],
    emailReach: 'Button corners only.',
    emailCaveat:
      "The email card's own corners are fixed. Still applies in dark mode, along with your fonts and your logo.",
    emailParts: [{ label: 'Button corners', selector: '.v-btn' }],
  },
}

/*
  THE EMAIL TAB CARRIES NO STANDING CAVEAT BLOCK. Adding one back needs a real argument.

  `EMAIL_TAB_CAVEATS` used to live here and render as an info Alert above the controls. Three
  lines; all three are now covered by the per-token `emailCaveat` fields above, which say the
  same things attached to the control the reader is touching, at the moment they touch it.

  Two of the three were plain restatements of `bg_color`, `primary_color` and `border_radius`
  and are not worth revisiting. The last was dark mode, which was the strongest of them: it
  is true of every colour at once, and it named the sun/moon toggle. Two placements were
  considered and both were dropped in favour of nothing:

    - ABOVE THE CONTROLS, where it started. Every sentence in it is a claim about the
      PREVIEW, so sitting atop the editor column it read as a claim about the CONTROLS, and
      duplicated the panels.
    - CAPTIONED UNDER THE PREVIEW, which fixed the misfiling and cost the form no space.
      Still a standing paragraph explaining a control the reader can simply press: the
      sun/moon toggle is inches away and demonstrates in one click what forty words assert.

  What remains true without it: each of the seven tokens states its own dark-mode behaviour,
  including which settings still apply (see `font` and `border_radius`). Nothing was lost
  except the aggregate.

  So the bar for new standing copy here is a fact that is a property of the PREVIEW rather
  than of a token, AND that the preview cannot simply be made to demonstrate. "It would be
  useful to know" is not the bar — that is what the panels are for.
*/
